import { ConnectionState } from "livekit-client";

export type LiveKitConnectCancelOrigin =
  | "none"
  | "timeout_guard"
  | "explicit_disconnect"
  | "stale_attempt";

export type LiveKitConnectFailureDecision =
  | {
      action: "ignore";
      reason: "stale_attempt" | "active_room" | "explicit_disconnect";
    }
  | {
      action: "retry";
      reason:
        | "token_issue"
        | "cancelled"
        | "timeout_guard"
        | "peer_connection"
        | "microphone_negotiation";
      message: string;
    }
  | {
      action: "fail";
      message: string;
    };

const LIVEKIT_CANCELLED_REASON = 3;

function readErrorField(error: unknown, field: string): unknown {
  if (typeof error !== "object" || error === null || !(field in error)) {
    return undefined;
  }

  return (error as Record<string, unknown>)[field];
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return typeof error === "string" ? error : "Unknown connection error";
}

export function isLiveKitCancelledConnectionError(error: unknown): boolean {
  const name = readErrorField(error, "name");
  const reasonName = readErrorField(error, "reasonName");
  const reason = readErrorField(error, "reason");
  const message = getErrorMessage(error).toLowerCase();

  if (reasonName === "Cancelled") {
    return true;
  }

  if (name === "ConnectionError" && reason === LIVEKIT_CANCELLED_REASON) {
    return true;
  }

  return name === "ConnectionError" && (message.includes("cancel") || message.includes("abort"));
}

export function isLikelyLiveKitTokenIssue(errorMessage: string): boolean {
  const lowerMsg = errorMessage.toLowerCase();

  return (
    lowerMsg.includes("token") ||
    lowerMsg.includes("auth") ||
    lowerMsg.includes("unauthorized") ||
    lowerMsg.includes("permission")
  );
}

export function isLikelyLiveKitPeerConnectionIssue(errorMessage: string): boolean {
  const lowerMsg = errorMessage.toLowerCase();

  return (
    lowerMsg.includes("could not establish pc connection") ||
    lowerMsg.includes("peer connection") ||
    lowerMsg.includes("ice connection")
  );
}

export function isLikelyMicrophoneNegotiationTimeout(errorMessage: string): boolean {
  const lowerMsg = errorMessage.toLowerCase();

  return lowerMsg.includes("negotiation timed out");
}

export function decideLiveKitConnectFailure({
  attemptId,
  activeAttemptId,
  cancelOrigin,
  error,
  retryCount,
  maxRetries,
  roomState,
  tokenIssueAlreadyHandled,
}: {
  attemptId: number;
  activeAttemptId: number;
  cancelOrigin: LiveKitConnectCancelOrigin;
  error: unknown;
  retryCount: number;
  maxRetries: number;
  roomState?: ConnectionState | null;
  tokenIssueAlreadyHandled?: boolean;
}): LiveKitConnectFailureDecision {
  if (attemptId !== activeAttemptId) {
    return { action: "ignore", reason: "stale_attempt" };
  }

  if (cancelOrigin === "explicit_disconnect") {
    return { action: "ignore", reason: "explicit_disconnect" };
  }

  const errorMessage = getErrorMessage(error);
  const canRetry = retryCount < maxRetries;

  if (isLikelyMicrophoneNegotiationTimeout(errorMessage) && canRetry) {
    return {
      action: "retry",
      reason: "microphone_negotiation",
      message: "Microphone setup timed out. Retrying...",
    };
  }

  if (roomState === ConnectionState.Connected || roomState === ConnectionState.Reconnecting) {
    return { action: "ignore", reason: "active_room" };
  }

  if (cancelOrigin === "timeout_guard" && canRetry) {
    return {
      action: "retry",
      reason: "timeout_guard",
      message: "Connection timed out. Retrying...",
    };
  }

  if (isLiveKitCancelledConnectionError(error) && canRetry) {
    return {
      action: "retry",
      reason: "cancelled",
      message: "Connection was interrupted. Retrying...",
    };
  }

  if (isLikelyLiveKitPeerConnectionIssue(errorMessage) && canRetry) {
    return {
      action: "retry",
      reason: "peer_connection",
      message: "Connection did not establish. Retrying...",
    };
  }

  if (isLikelyLiveKitTokenIssue(errorMessage) && !tokenIssueAlreadyHandled && canRetry) {
    return {
      action: "retry",
      reason: "token_issue",
      message: "Refreshing connection. Retrying...",
    };
  }

  return { action: "fail", message: errorMessage };
}
